import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GraphQLState } from '../store/graphql-reducer'
import { SiteState } from '../store/site-reducer'
import { OauthState } from '../store/oauth-reducer'
import SiteWrapper from '../components/sitewrapper'
import Layout from '../components/layout'
import Intro from '../components/intro'

import {
  getSelectedAccountAsync,
  logActionAsync,
  switchAccountAsync,
} from '../store/actions'

import ProfileSettings, { Preferences } from '../components/profile-settings'
import TeamMatesSettings from '../components/teammates-settings'
import ConnectedWebsitesSettings from '../components/connected-websites-settings'
import BillingAndAccountSettings from '../components/billing-and-account-settings'
import AccountSettings, { TheForm } from '../components/account-settings'
import ToggleBox from '../components/toggle-box'
import Button from '../components/button'
import Modal, { ModalHead } from '../components/modal'
import styles from '../component-styles/settings-page.module.scss'
import { useQuery } from '../helpers'
import {
  addEnterpriseAccount,
  defaultEnterpriseFields,
} from '../api-client/settings-client'

export default function Settings(): React.ReactElement {
  const [openSection, setOpenSection] = useState('')
  const [active, setActive] = useState(false)

  const dispatch = useDispatch()

  const {
    site,
    oauth,
    graphql,
  }: {
    site: SiteState
    graphql: GraphQLState
    oauth: OauthState
  } = useSelector((s: any): any => s)
  const { currentAccount, currentUser } = graphql
  const currentAccountPermission = currentUser?.currentAccountPermission || ''
  const profileImgLink = currentUser?.profileImgLink || ''

  const {
    credentials,
    state: { oauthJourney },
  } = oauth

  const { selected } = site
  const query = useQuery()

  useEffect(() => {
    dispatch(getSelectedAccountAsync())
  }, [])

  useEffect(() => {
    const accId = query?.get('accountID')

    if (
      !accId ||
      !currentAccount ||
      !currentAccount?.accountID ||
      currentAccount.accountID === accId
    ) {
      return
    }

    dispatch(switchAccountAsync(accId))
    // eslint-disable-next-line
  }, [query, currentAccount])

  const defaultOpen = query?.get('show')

  useEffect(() => {
    if (defaultOpen) {
      setOpenSection(defaultOpen)
    }
  }, [defaultOpen])

  const { billingType } = selected || {}
  const isEnterpriseAccount = billingType === 'enterprise'

  const isSupportUser = currentAccountPermission === 'support'

  const isAdminUser = currentAccountPermission === 'admin'

  const toggleActive = () => {
    setActive(!active)
  }

  return (
    <SiteWrapper>
      <Layout>
        <Intro title="Settings">
          <p>Manage and customise your account.</p>
        </Intro>
        <div className={styles.container}>
          <ToggleBox
            noPadding
            className={styles.settingsToggleBox}
            heading={
              <div className={styles.settingHeader}>
                <img
                  src={profileImgLink || '/missig-avatar.png'}
                  alt="Profile"
                  style={{ borderRadius: '50%' }}
                />
                <div className={styles.headerText}>
                  <h2>Profile</h2>
                  <span>Update your contact information.</span>
                </div>
              </div>
            }
            open={openSection === 'profile'}
            onToggle={() => {
              if (openSection === 'profile') {
                setOpenSection('')
              } else {
                setOpenSection('profile')
                dispatch(
                  logActionAsync({
                    action: 'open-settings-section',
                    longDescription: '{"sectionOpened": "profile"}',
                    websiteSection: 'settings',
                  }),
                )
              }
            }}
          >
            <ProfileSettings className={styles.settingsToggleBoxInner} />
          </ToggleBox>
          <ToggleBox
            noPadding
            className={styles.settingsToggleBox}
            heading={
              <div className={styles.settingHeader}>
                <div className={styles.emoji}>
                  <span role="img" aria-label="Preferences">
                    ❤️
                  </span>
                </div>
                <div className={styles.headerText}>
                  <h2>Preferences</h2>
                  <span>Set your homepage and notifications.</span>
                </div>
              </div>
            }
            open={openSection === 'notifications'}
            onToggle={() => {
              if (openSection === 'notifications') {
                setOpenSection('')
              } else {
                setOpenSection('notifications')
                dispatch(
                  logActionAsync({
                    action: 'open-settings-section',
                    longDescription: '{"sectionOpened": "preferences"}',
                    websiteSection: 'settings',
                  }),
                )
              }
            }}
          >
            <Preferences
              className={styles.settingsToggleBoxInner}
              showValue={defaultOpen}
            />
          </ToggleBox>
          {(isAdminUser || isSupportUser) && (
            <>
              <ToggleBox
                noPadding
                className={styles.settingsToggleBox}
                heading={
                  <div className={styles.settingHeader}>
                    <div className={styles.emoji}>
                      <span role="img" aria-label="Users">
                        👥
                      </span>
                    </div>
                    <div className={styles.headerText}>
                      <h2>Users</h2>
                      <span>Add, remove and reassign users.</span>
                    </div>
                  </div>
                }
                open={openSection === 'team'}
                onToggle={() => {
                  if (openSection === 'team') {
                    setOpenSection('')
                  } else {
                    setOpenSection('team')
                    dispatch(
                      logActionAsync({
                        action: 'open-settings-section',
                        longDescription: '{"sectionOpened": "team"}',
                        websiteSection: 'settings',
                      }),
                    )
                  }
                }}
              >
                <TeamMatesSettings className={styles.settingsToggleBoxInner} />
              </ToggleBox>
              <ToggleBox
                noPadding
                className={styles.settingsToggleBox}
                heading={
                  <div className={styles.settingHeader}>
                    <div className={styles.emoji}>
                      <span role="img" aria-label="Account features">
                        🏛️
                      </span>
                    </div>
                    <div className={styles.headerText}>
                      <h2>Account features</h2>
                      <span>
                        Update details, features and preferences for all users.
                      </span>
                    </div>
                  </div>
                }
                open={openSection === 'billing'}
                onToggle={() => {
                  if (openSection === 'billing') {
                    setOpenSection('')
                  } else {
                    setOpenSection('billing')
                    dispatch(
                      logActionAsync({
                        action: 'open-settings-section',
                        longDescription: '{"sectionOpened": "billing"}',
                        websiteSection: 'settings',
                      }),
                    )
                  }
                }}
              >
                <BillingAndAccountSettings
                  isEnterpriseAccount={isEnterpriseAccount}
                  className={styles.settingsToggleBoxInner}
                />
              </ToggleBox>
              <ToggleBox
                noPadding
                className={styles.settingsToggleBox}
                heading={
                  <div className={styles.settingHeader}>
                    <div className={styles.emoji}>
                      <span role="img" aria-label="Connections">
                        📡
                      </span>
                    </div>
                    <div className={styles.headerText}>
                      <h2>Connected websites</h2>
                      <span>
                        Connect, reconnect or remove analytics accounts.
                      </span>
                    </div>
                  </div>
                }
                open={
                  (credentials !== '' && oauthJourney) ||
                  openSection === 'connect'
                }
                onToggle={() => {
                  if (openSection === 'connect') {
                    setOpenSection('')
                  } else {
                    setOpenSection('connect')
                    dispatch(
                      logActionAsync({
                        action: 'open-settings-section',
                        longDescription: '{"sectionOpened": "connections"}',
                        websiteSection: 'settings',
                      }),
                    )
                  }
                }}
              >
                <ConnectedWebsitesSettings
                  className={styles.settingsToggleBoxInner}
                />
              </ToggleBox>
            </>
          )}
          {isSupportUser && (
            <ToggleBox
              noPadding
              className={styles.settingsToggleBox}
              heading={
                <div className={styles.settingHeader}>
                  <div className={styles.emoji}>
                    <span role="img" aria-label="Enterprise client details">
                      📝
                    </span>
                  </div>
                  <div className={styles.headerText}>
                    <h2>Enterprise client details</h2>
                    <span>Review contract details.</span>
                  </div>
                </div>
              }
              open={openSection === 'accountSettings'}
              onToggle={() => {
                if (openSection === 'accountSettings') {
                  setOpenSection('')
                } else {
                  setOpenSection('accountSettings')
                  dispatch(
                    logActionAsync({
                      action: 'open-settings-section',
                      longDescription:
                        '{"sectionOpened": "enterprise client details"}',
                      websiteSection: 'settings',
                    }),
                  )
                }
              }}
            >
              <AccountSettings className={styles.settingsToggleBoxInner} />
            </ToggleBox>
          )}
        </div>

        {isSupportUser && (
          <>
            {active && (
              <Modal active={active} toggleActive={toggleActive} type="white">
                <ModalHead>
                  <h3>Create a new enterprise client</h3>
                  <p>
                    This form should only be used when a new contract has been
                    signed.
                  </p>
                </ModalHead>
                <TheForm
                  readOnly={false}
                  modal
                  fields={defaultEnterpriseFields}
                  onCancel={toggleActive}
                  onSave={(newData: EnterpriseBillingDetails) => {
                    dispatch(addEnterpriseAccount(newData))
                    setActive(!active)
                  }}
                />
              </Modal>
            )}

            <Button
              type="flatPink"
              // className={styles.noMargin}
              align="right"
              onClick={() => {
                toggleActive()
              }}
            >
              Create a new enterprise client
            </Button>
          </>
        )}
      </Layout>
    </SiteWrapper>
  )
}
